import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import './AdminPromo.css';

const KIND_OPTIONS = [
    { value: 'percent', label: 'Скидка в процентах', unit: '%', hint: 'Например, −30% на любую оплату' },
    { value: 'fixed', label: 'Скидка в рублях', unit: '₽', hint: 'Например, −200 ₽ — удобно для партнёров и блогеров' },
    { value: 'bonus_days', label: 'Бонусные дни к оплате', unit: 'дней', hint: 'Оплатил 30 дней — получил 30 + N. Цена не падает, ценность растёт' },
    { value: 'free_days', label: 'Бесплатный доступ', unit: 'дней', hint: '«Расширенный» на N дней без оплаты — пробный период для конференций' },
];
const AUDIENCE = { all: 'Все', new: 'Только первая оплата', renewal: 'Только продление' };
const SOURCE_TABS = [
    { value: 'admin', label: 'Промокоды' },
    { value: 'referral', label: 'Реферальные' },
    { value: 'winback', label: 'Письма «вернитесь»' },
];

const rub = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₽`;
const dateRu = (iso) => (iso ? new Date(iso).toLocaleDateString('ru-RU') : '');
const toIsoEndOfDay = (d) => (d ? new Date(`${d}T23:59:59`).toISOString() : null);
const toIsoStartOfDay = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : null);

const EMPTY_FORM = {
    mode: 'single', code: '', count: 50, prefix: '', campaign: '', kind: 'percent', value: 20,
    audience: 'all', starts: '', ends: '', maxUses: '', domain: '', note: '',
};

async function downloadCsv(campaign) {
    const res = await api.get('/admin/promo-codes/export', {
        params: campaign ? { campaign } : {},
        responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    // Латиница: часть браузеров теряет имя файла с русскими буквами и сохраняет его без расширения
    a.download = `promo_${campaign ? 'campaign_' : 'all_'}${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function previewText(form, price) {
    const v = Number(form.value) || 0;
    if (form.kind === 'percent') return `Месяц будет стоить ${rub(Math.max(Math.round(price * (100 - v) / 100), 0))} вместо ${rub(price)}`;
    if (form.kind === 'fixed') return `Месяц будет стоить ${rub(Math.max(price - v, 0))} вместо ${rub(price)}`;
    if (form.kind === 'bonus_days') return `За ${rub(price)} пользователь получит ${30 + v} дней вместо 30`;
    return `Тариф включится сразу на ${v} дней, без оплаты`;
}

function PromoForm({ price, onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const kind = KIND_OPTIONS.find((k) => k.value === form.kind);
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        const payload = {
            campaign: form.campaign.trim() || null,
            kind: form.kind,
            value: Number(form.value),
            audience: form.audience,
            starts_at: toIsoStartOfDay(form.starts),
            ends_at: toIsoEndOfDay(form.ends),
            max_uses: form.maxUses === '' ? null : Number(form.maxUses),
            email_domain: form.domain.trim() || null,
            note: form.note.trim() || null,
        };
        setBusy(true);
        try {
            if (form.mode === 'single') {
                const res = await api.post('/admin/promo-codes', { ...payload, code: form.code.trim() || null });
                setResult({ codes: [res.data.code], campaign: payload.campaign });
            } else {
                const res = await api.post('/admin/promo-codes/batch', {
                    ...payload, count: Number(form.count), prefix: form.prefix.trim() || null,
                });
                setResult({ codes: res.data.codes, campaign: payload.campaign });
            }
            onCreated();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось создать промокод'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="admin-modal-overlay" role="presentation" onClick={() => !busy && onClose()}>
            <div className="admin-modal admin-modal--wide promo-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <form onSubmit={submit}>
                    <div className="admin-modal__body">
                        <h3>{result ? 'Промокоды созданы' : 'Новый промокод'}</h3>
                        {result ? (
                            <div className="promo-result">
                                <p>Создано: <b>{result.codes.length}</b>{result.campaign ? <> в кампании «{result.campaign}»</> : null}.</p>
                                <textarea readOnly value={result.codes.join('\n')} rows={Math.min(result.codes.length, 8)} onFocus={(e) => e.target.select()} />
                                {result.codes.length > 1 && (
                                    <button type="button" className="btn btn--secondary" onClick={() => downloadCsv(result.campaign)}>
                                        Скачать CSV{result.campaign ? ' кампании' : ''}
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="promo-tabs" role="tablist">
                                    <button type="button" role="tab" aria-selected={form.mode === 'single'} className={form.mode === 'single' ? 'is-active' : ''} onClick={() => set({ mode: 'single', maxUses: '' })}>
                                        Один код — для рассылки, соцсетей
                                    </button>
                                    <button type="button" role="tab" aria-selected={form.mode === 'batch'} className={form.mode === 'batch' ? 'is-active' : ''} onClick={() => set({ mode: 'batch', maxUses: '1' })}>
                                        Пачка уникальных кодов — раздать по одному
                                    </button>
                                </div>
                                <div className="promo-grid">
                                    {form.mode === 'single' ? (
                                        <label className="promo-field">
                                            <span>Код</span>
                                            <input value={form.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} placeholder="Например, VESNA2026 (пусто — сгенерировать)" />
                                        </label>
                                    ) : (
                                        <>
                                            <label className="promo-field">
                                                <span>Сколько кодов</span>
                                                <input type="number" min="1" max="1000" value={form.count} onChange={(e) => set({ count: e.target.value })} />
                                            </label>
                                            <label className="promo-field">
                                                <span>Префикс</span>
                                                <input value={form.prefix} onChange={(e) => set({ prefix: e.target.value.toUpperCase() })} placeholder="CONF → CONF-7KQ3-M9XD" />
                                            </label>
                                        </>
                                    )}
                                    <label className="promo-field">
                                        <span>Кампания</span>
                                        <input value={form.campaign} onChange={(e) => set({ campaign: e.target.value })} placeholder="Конференция EdTech, Блогер Иванов…" />
                                    </label>
                                </div>
                                <div className="promo-grid">
                                    <label className="promo-field">
                                        <span>Бонус</span>
                                        <select value={form.kind} onChange={(e) => set({ kind: e.target.value, value: e.target.value === 'percent' ? 20 : e.target.value === 'fixed' ? 100 : 14 })}>
                                            {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                                        </select>
                                        <small>{kind?.hint}</small>
                                    </label>
                                    <label className="promo-field">
                                        <span>Размер, {kind?.unit}</span>
                                        <input type="number" min="1" max={form.kind === 'percent' ? 100 : undefined} value={form.value} onChange={(e) => set({ value: e.target.value })} required />
                                    </label>
                                    <label className="promo-field">
                                        <span>Для кого</span>
                                        <select value={form.audience} onChange={(e) => set({ audience: e.target.value })}>
                                            {Object.entries(AUDIENCE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                        </select>
                                    </label>
                                </div>
                                <div className="promo-preview">{previewText(form, price)}</div>
                                <div className="promo-grid">
                                    <label className="promo-field">
                                        <span>Действует с</span>
                                        <input type="date" value={form.starts} onChange={(e) => set({ starts: e.target.value })} />
                                    </label>
                                    <label className="promo-field">
                                        <span>Действует по</span>
                                        <input type="date" value={form.ends} onChange={(e) => set({ ends: e.target.value })} />
                                        <small>Пусто — бессрочно</small>
                                    </label>
                                    <label className="promo-field">
                                        <span>{form.mode === 'batch' ? 'Активаций на каждый код' : 'Всего активаций'}</span>
                                        <input type="number" min="1" value={form.maxUses} onChange={(e) => set({ maxUses: e.target.value })} placeholder="Без лимита" />
                                        {form.kind === 'free_days' && form.maxUses === '' && <small className="promo-warn">Бесплатный доступ без лимита активаций — рискованно</small>}
                                    </label>
                                </div>
                                <div className="promo-grid">
                                    <label className="promo-field">
                                        <span>Только для почт домена</span>
                                        <input value={form.domain} onChange={(e) => set({ domain: e.target.value })} placeholder="university.ru — необязательно" />
                                    </label>
                                    <label className="promo-field">
                                        <span>Заметка</span>
                                        <input value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="Для себя: откуда и зачем" />
                                    </label>
                                </div>
                                <p className="promo-hint">Каждый пользователь может применить код один раз. Промокоды не суммируются со скидками других кодов.</p>
                            </>
                        )}
                        {error && <div className="admin-notice admin-notice--error" role="alert">{error}</div>}
                    </div>
                    <div className="admin-modal__footer">
                        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>{result ? 'Готово' : 'Отмена'}</button>
                        {!result && (
                            <button type="submit" className="btn btn--success" disabled={busy}>
                                {busy ? 'Создаём…' : form.mode === 'single' ? 'Создать промокод' : `Создать ${form.count || 0} кодов`}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

function Redemptions({ promo, onClose }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get(`/admin/promo-codes/${promo.id}/redemptions`).then((res) => setRows(res.data)).catch(() => setRows([]));
    }, [promo.id]);
    const statusText = { used: 'оплачено', reserved: 'ждёт оплаты', released: 'оплата не завершена' };
    return (
        <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
            <div className="admin-modal admin-modal--wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="admin-modal__body">
                    <h3>Кто использовал {promo.code}</h3>
                    {rows === null ? <p>Загрузка…</p> : rows.length === 0 ? <p className="admin-modal-empty">Код ещё никто не применял.</p> : (
                        <div className="admin-table-scroll">
                            <table className="admin-table promo-table promo-table--small">
                                <thead><tr><th>Пользователь</th><th>Статус</th><th>Срок</th><th>Оплачено</th><th>Скидка</th><th>Дата</th></tr></thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.id}>
                                            <td>{r.email || `ID ${r.teacher_id}`}</td>
                                            <td>{statusText[r.status] || r.status}</td>
                                            <td>{promo.kind === 'free_days' ? `${promo.value} дн. бесплатно` : `${r.months} мес.${r.bonus_days ? ` +${r.bonus_days} дн.` : ''}`}</td>
                                            <td>{rub(r.final_amount)}</td>
                                            <td>{promo.kind === 'free_days' ? '—' : rub(r.discount_amount)}</td>
                                            <td>{dateRu(r.used_at || r.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="admin-modal__footer">
                    <button type="button" className="btn btn--secondary" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
}

function BillingSettings({ settings, onSaved }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        if (settings) {
            setForm({
                price: settings.tariff_advanced_price,
                d3: settings.discounts['3'], d6: settings.discounts['6'], d12: settings.discounts['12'],
                refOn: settings.referral_enabled, refFriend: settings.referral_friend_days, refOwner: settings.referral_referrer_days,
                wbOn: settings.winback_enabled, wbDiscount: settings.winback_discount, wbDays: settings.winback_valid_days,
            });
        }
    }, [settings]);

    if (!form) return null;
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const save = async () => {
        setBusy(true);
        setNotice(null);
        try {
            await api.put('/admin/billing/settings', {
                tariff_advanced_price: form.price,
                discounts: { 3: form.d3, 6: form.d6, 12: form.d12 },
                referral_enabled: form.refOn,
                referral_friend_days: form.refFriend,
                referral_referrer_days: form.refOwner,
                winback_enabled: form.wbOn,
                winback_discount: form.wbDiscount,
                winback_valid_days: form.wbDays,
            });
            setNotice({ type: 'success', text: 'Настройки сохранены.' });
            onSaved();
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось сохранить настройки') });
        } finally {
            setBusy(false);
        }
    };

    const runWinback = async () => {
        if (!window.confirm('Разослать письма «вернитесь» сейчас всем, у кого тариф закончился 1–60 дней назад и кто согласился на рассылку?')) return;
        setBusy(true);
        try {
            const res = await api.post('/admin/winback/run');
            setNotice({ type: 'success', text: `Отправлено писем: ${res.data.sent}${res.data.failed ? `, не удалось: ${res.data.failed}` : ''}.` });
            onSaved();
        } catch (err) {
            setNotice({ type: 'error', text: getApiErrorMessage(err, 'Не удалось разослать письма') });
        } finally {
            setBusy(false);
        }
    };

    const num = (key, props = {}) => (
        <input type="number" min="0" value={form[key]} onChange={(e) => set({ [key]: e.target.value })} {...props} />
    );

    return (
        <details className="promo-settings">
            <summary>Цены, скидки за срок, рефералы и письма «вернитесь»</summary>
            <div className="promo-settings__body">
                <div className="promo-settings__block">
                    <h4>Цена и скидки за срок</h4>
                    <div className="promo-grid">
                        <label className="promo-field"><span>Месяц «Расширенного», ₽</span>{num('price', { min: 1 })}</label>
                        <label className="promo-field"><span>Скидка за 3 месяца, %</span>{num('d3', { max: 90 })}</label>
                        <label className="promo-field"><span>Скидка за 6 месяцев, %</span>{num('d6', { max: 90 })}</label>
                        <label className="promo-field"><span>Скидка за 12 месяцев, %</span>{num('d12', { max: 90 })}</label>
                    </div>
                </div>
                <div className="promo-settings__block">
                    <h4>Реферальная программа</h4>
                    <label className="promo-check"><input type="checkbox" checked={form.refOn} onChange={(e) => set({ refOn: e.target.checked })} /> Включена — у каждого пользователя в профиле личная ссылка и код</label>
                    <div className="promo-grid">
                        <label className="promo-field"><span>Другу к первой оплате, дней</span>{num('refFriend')}</label>
                        <label className="promo-field"><span>Автору после оплаты друга, дней</span>{num('refOwner')}</label>
                    </div>
                    <p className="promo-hint">Начислено наград: {settings.stats.referral_rewards}.</p>
                </div>
                <div className="promo-settings__block">
                    <h4>Письма «вернитесь со скидкой»</h4>
                    <label className="promo-check"><input type="checkbox" checked={form.wbOn} onChange={(e) => set({ wbOn: e.target.checked })} /> Отправлять автоматически (проверка раз в 6 часов)</label>
                    <div className="promo-grid">
                        <label className="promo-field"><span>Скидка в письме, %</span>{num('wbDiscount', { min: 1, max: 90 })}</label>
                        <label className="promo-field"><span>Код действует, дней</span>{num('wbDays', { min: 1 })}</label>
                    </div>
                    <p className="promo-hint">
                        Письмо с персональным одноразовым промокодом уходит один раз, если оплаченный «Расширенный» закончился 1–60 дней назад
                        и человек согласился на рассылку. Отправлено: {settings.stats.winback_sent}, вернулись и оплатили: {settings.stats.winback_returned}.
                    </p>
                    <button type="button" className="btn btn--secondary" onClick={runWinback} disabled={busy}>Разослать сейчас</button>
                </div>
                {notice && <div className={`admin-notice admin-notice--${notice.type}`} role="status">{notice.text}</div>}
                <button type="button" className="btn btn--success" onClick={save} disabled={busy}>Сохранить настройки</button>
            </div>
        </details>
    );
}

function AdminPromo() {
    const [source, setSource] = useState('admin');
    const [campaign, setCampaign] = useState('');
    const [data, setData] = useState({ items: [], campaigns: [] });
    const [settings, setSettings] = useState(null);
    const [settingsError, setSettingsError] = useState('');
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [historyFor, setHistoryFor] = useState(null);
    const [copied, setCopied] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/admin/promo-codes', { params: { source } });
            setData(res.data);
            setError('');
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить промокоды'));
        }
    }, [source]);

    const loadSettings = useCallback(() => {
        api.get('/admin/billing/settings')
            .then((res) => { setSettings(res.data); setSettingsError(''); })
            .catch((err) => setSettingsError(getApiErrorMessage(err, 'Не удалось загрузить настройки цен')));
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadSettings(); }, [loadSettings]);

    const items = useMemo(
        () => data.items.filter((i) => !campaign || (i.campaign || 'Без кампании') === campaign),
        [data.items, campaign],
    );

    const toggle = async (promo) => {
        try {
            await api.patch(`/admin/promo-codes/${promo.id}`, { is_active: !promo.is_active });
            load();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось изменить промокод'));
        }
    };

    const copy = async (code) => {
        try { await navigator.clipboard.writeText(code); setCopied(code); } catch { setCopied(null); }
        setTimeout(() => setCopied(null), 1500);
    };

    const expired = (p) => p.ends_at && new Date(p.ends_at) < new Date();

    return (
        <section className="admin-main-card promo-admin">
            <div className="promo-admin__head">
                <h2 className="admin-main-card__title">Промокоды</h2>
                <button type="button" className="btn btn--success" onClick={() => setShowForm(true)}>＋ Создать промокод</button>
            </div>
            <div className="promo-admin__body">
                <p className="promo-intro">
                    Пользователь вводит код в профиле при оплате «Расширенного» или переходит по ссылке
                    <code>app.svodx.ru/profile?promo=КОД</code> — код подставится сам. Сумму считает сервер, код засчитывается только после успешной оплаты.
                </p>

                {settingsError && <div className="admin-notice admin-notice--error" role="alert">{settingsError}</div>}
                <BillingSettings settings={settings} onSaved={() => { loadSettings(); load(); }} />

                <div className="promo-filters">
                    <div className="promo-tabs" role="tablist">
                        {SOURCE_TABS.map((t) => (
                            <button key={t.value} type="button" role="tab" aria-selected={source === t.value} className={source === t.value ? 'is-active' : ''} onClick={() => { setSource(t.value); setCampaign(''); }}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {source === 'admin' && data.campaigns.length > 0 && (
                        <div className="promo-filters__right">
                            <select value={campaign} onChange={(e) => setCampaign(e.target.value)} aria-label="Кампания">
                                <option value="">Все кампании</option>
                                {data.campaigns.map((c) => <option key={c.campaign} value={c.campaign}>{c.campaign}</option>)}
                            </select>
                            <button type="button" className="btn btn--secondary" onClick={() => downloadCsv(campaign && campaign !== 'Без кампании' ? campaign : null)}>Скачать CSV</button>
                        </div>
                    )}
                </div>

                {source === 'admin' && data.campaigns.length > 0 && !campaign && (
                    <div className="promo-campaigns">
                        {data.campaigns.map((c) => (
                            <button key={c.campaign} type="button" className="promo-campaign" onClick={() => setCampaign(c.campaign)}>
                                <b>{c.campaign}</b>
                                <span>{c.codes} {c.codes === 1 ? 'код' : 'кодов'} · {c.uses} оплат</span>
                                <span className="promo-campaign__money">{rub(c.revenue)} <small>скидок {rub(c.discount_total)}</small></span>
                            </button>
                        ))}
                    </div>
                )}

                {error && <div className="admin-notice admin-notice--error" role="alert">{error}</div>}

                {items.length === 0 ? (
                    <p className="admin-modal-empty">
                        {source === 'admin' ? 'Промокодов пока нет. Нажмите «Создать промокод».'
                            : source === 'referral' ? 'Реферальные коды появятся, когда пользователи откроют блок «Пригласите коллег» в профиле.'
                                : 'Писем «вернитесь» ещё не отправляли.'}
                    </p>
                ) : (
                    <div className="admin-table-scroll">
                        <table className="admin-table promo-table">
                            <thead>
                                <tr>
                                    <th>Код</th>
                                    <th>Бонус</th>
                                    <th>Кампания</th>
                                    <th>Действует до</th>
                                    <th>Оплат</th>
                                    <th>Выручка</th>
                                    <th>Статус</th>
                                    <th aria-label="Действия" />
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id} className={!p.is_active || expired(p) ? 'is-off' : ''}>
                                        <td>
                                            <button type="button" className="promo-code" onClick={() => copy(p.code)} title="Скопировать">
                                                {p.code}{copied === p.code ? ' ✓' : ''}
                                            </button>
                                            {p.audience !== 'all' && <small className="promo-sub">{AUDIENCE[p.audience]}</small>}
                                            {p.email_domain && <small className="promo-sub">@{p.email_domain}</small>}
                                        </td>
                                        <td>{p.label}</td>
                                        <td>{p.campaign || '—'}</td>
                                        <td>{p.ends_at ? dateRu(p.ends_at) : 'бессрочно'}</td>
                                        <td>{p.uses}{p.max_uses ? ` / ${p.max_uses}` : ''}{p.reserved ? <small className="promo-sub">+{p.reserved} ждут оплаты</small> : null}</td>
                                        <td>{rub(p.revenue)}</td>
                                        <td>
                                            <span className={`status-mock status-mock--${p.is_active && !expired(p) ? 'active' : 'blocked'}`}>
                                                {!p.is_active ? 'ВЫКЛ' : expired(p) ? 'ИСТЁК' : 'ВКЛ'}
                                            </span>
                                        </td>
                                        <td className="promo-actions">
                                            <button type="button" className="btn btn--secondary" onClick={() => setHistoryFor(p)}>История</button>
                                            {source === 'admin' && (
                                                <button type="button" className="btn btn--secondary" onClick={() => toggle(p)}>
                                                    {p.is_active ? 'Выключить' : 'Включить'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showForm && (
                <PromoForm
                    price={settings?.tariff_advanced_price || 500}
                    onClose={() => setShowForm(false)}
                    onCreated={load}
                />
            )}
            {historyFor && <Redemptions promo={historyFor} onClose={() => setHistoryFor(null)} />}
        </section>
    );
}

export default AdminPromo;
